import { Link, useNavigate } from 'react-router-dom'
import { Store, UserCircle, ChevronDown, ShoppingBag, Bell, Package, Scissors } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useOrg } from '@/context/OrgContext'
import { useNotifications } from '@/hooks/useNotifications'
import NotifBadge from '@/components/NotifBadge'
import ProfileMenu from '@/components/ProfileMenu'

function useDropdown() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return { open, setOpen, ref }
}

function GuestMenu() {
  const { open, setOpen, ref } = useDropdown()

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-md hover:bg-white/10 transition-colors"
      >
        <UserCircle size={20} />
        <span className="text-sm font-medium">Pembeli</span>
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50">
          <Link to="/login" onClick={() => setOpen(false)}
            className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
            Masuk
          </Link>
          <div className="border-t border-gray-100" />
          <Link to="/register" onClick={() => setOpen(false)}
            className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 transition-colors">
            Daftar
          </Link>
          <div className="border-t border-gray-100" />
          <Link to="/cek-pesanan" onClick={() => setOpen(false)}
            className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
            Cek Status Pesanan
          </Link>
        </div>
      )}
    </div>
  )
}

function UserNav({ userId }: { userId: string }) {
  const { unreadCount, markAllRead } = useNotifications(userId)
  const navigate = useNavigate()

  return (
    <div className="flex items-center gap-1">
      <Link to="/my-order"
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-md hover:bg-white/10 transition-colors text-sm font-medium">
        <ShoppingBag size={16} />
        <span className="hidden sm:inline">Order Saya</span>
      </Link>
      <button
        onClick={() => { markAllRead(); navigate('/notifikasi') }}
        className="relative flex items-center px-2 py-1.5 rounded-md hover:bg-white/10 transition-colors"
      >
        <NotifBadge show={unreadCount > 0}>
          <Bell size={18} />
        </NotifBadge>
      </button>
    </div>
  )
}

export default function Nav() {
  const { user } = useAuth()
  const { org, isRental, isJasa } = useOrg()
  const nama = user?.user_metadata?.nama ?? user?.email ?? 'Akun'
  const avatarUrl = user?.user_metadata?.avatar_url

  return (
    <nav className="text-white shadow-md sticky top-0 z-50" style={{ backgroundColor: 'var(--color-primary, #2563eb)' }}>
      <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-bold text-lg">
          {org?.logo_url
            ? <img src={org.logo_url} alt={org.nama} className="w-6 h-6 rounded object-contain" />
            : <Store size={22} />
          }
          {org?.nama ?? 'Toko'}
        </Link>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-2">
          {isRental && (
            <>
              <Link to="/sewa"
                className="flex items-center gap-1.5 text-sm font-medium text-white/80 hover:text-white transition-colors px-2 py-1.5">
                <Package size={15} />
                Sewa
              </Link>
              <div className="w-px h-4 bg-white/30" />
            </>
          )}
          {isJasa && (
            <>
              <Link to="/jasa"
                className="flex items-center gap-1.5 text-sm font-medium text-white/80 hover:text-white transition-colors px-2 py-1.5">
                <Scissors size={15} />
                Layanan
              </Link>
              <div className="w-px h-4 bg-white/30" />
            </>
          )}
          <Link to="/cek-pesanan"
            className="text-sm font-medium text-white/80 hover:text-white transition-colors px-2 py-1.5">
            Cek Pesanan
          </Link>
          <div className="w-px h-4 bg-white/30" />
          {user ? (
            <>
              <UserNav userId={user.id} />
              <div className="w-px h-4 bg-white/30" />
              <ProfileMenu nama={nama} avatarUrl={avatarUrl} role="Pelanggan" />
            </>
          ) : (
            <>
              <Link to="/login"
                className="px-4 py-1.5 rounded-md text-sm font-medium border border-white/60 hover:bg-white/10 transition-colors">
                Masuk
              </Link>
              <Link to="/register"
                className="px-4 py-1.5 rounded-md text-sm font-medium bg-white text-blue-600 hover:bg-blue-50 transition-colors">
                Daftar
              </Link>
            </>
          )}
        </div>

        {/* Mobile */}
        <div className="md:hidden flex items-center gap-1">
          {isRental && (
            <Link to="/sewa"
              className="flex items-center gap-1 px-2 py-1.5 rounded-md hover:bg-white/10 transition-colors text-sm font-medium">
              <Package size={16} />
              <span>Sewa</span>
            </Link>
          )}
          {isJasa && (
            <Link to="/jasa"
              className="flex items-center gap-1 px-2 py-1.5 rounded-md hover:bg-white/10 transition-colors text-sm font-medium">
              <Scissors size={16} />
              <span>Layanan</span>
            </Link>
          )}
          {user ? (
            <>
              <UserNav userId={user.id} />
              <ProfileMenu nama={nama} avatarUrl={avatarUrl} role="Pelanggan" />
            </>
          ) : (
            <GuestMenu />
          )}
        </div>
      </div>
    </nav>
  )
}
